import AntIcon from "@/components/icons/AntIcon";
import { NamePath } from "@rc-component/form/es/interface";
import { Flex, Form, Popover, Radio } from "antd";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CalcCheckableTag } from "./CalcCheckableTag";

const NO_OPTION = "NO_OPTION";

export type CalcTagOption = {
  value: string;
  label: string;
  tagLabel?: string;
};

type Props = {
  name: NamePath;
  optionName: NamePath;
  label: string;
  optionsLabel: string;
  options: CalcTagOption[];
  extra?: ReactNode;
  onActivate?: () => void;
};

export const CalcOptionTag = ({ name, optionName, label, optionsLabel, options, extra, onActivate }: Props) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const form = Form.useFormInstance();
  const checked = Form.useWatch(name, form);
  const selectedValue = Form.useWatch(optionName, form);

  const selectedOption = options.find((option) => option.value === selectedValue);

  const handleChange = (value: string) => {
    if (value === NO_OPTION) {
      form.setFieldValue(name, false);
      setOpen(false);
      return;
    }

    if (!checked) {
      onActivate?.();
    }

    form.setFields([
      { name, value: true },
      { name: optionName, value },
    ]);

    if (!extra) {
      setOpen(false);
    }
  };

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      open={open}
      onOpenChange={setOpen}
      content={
        <Flex vertical gap="small" style={{ minWidth: 200 }}>
          <Form.Item label={optionsLabel} style={{ marginBottom: 0 }}>
            <Radio.Group
              orientation="vertical"
              value={checked ? selectedValue : NO_OPTION}
              options={[
                { value: NO_OPTION, label: t("calc.helpers.noReinsurance") },
                ...options.map(({ value, label }) => ({ value, label })),
              ]}
              onChange={(e) => handleChange(e.target.value)}
            />
          </Form.Item>

          {checked && extra}
        </Flex>
      }
    >
      <CalcCheckableTag checked={!!checked}>
        {checked && selectedOption ? `${label}: ${selectedOption.tagLabel ?? selectedOption.label}` : label}
        <AntIcon type="down" style={{ marginLeft: 6, fontSize: 10 }} />
      </CalcCheckableTag>
    </Popover>
  );
};
